import React, {useEffect, useRef} from 'react';
import {createPortal} from 'react-dom';
import {observer} from 'mobx-react';
import s from '../../../s';
import {logger} from '../../../../logger';
import {add_to_cart} from '../../../api';
import {PlatePreview} from '../ep-template/ep-template';
import {platePreviewMaxWidthPx, plateSummary} from './inst-plate-rules';
import {instPlateStore} from './inst-plate.store';

export const INST_PLATE_CONFIRM_TITLE = 'Customization Approval';
export const INST_PLATE_CONFIRM_TEXT = 'Please confirm that the custom plate proof is what you want.';
export const INST_PLATE_CONFIRM_SIZE_LABEL = 'Plate:';

const confirmAndAddToCart = async () => {
    if (s.ui.atc_busy) {
        return;
    }

    const buyNow = instPlateStore.confirm();
    try {
        await add_to_cart(buyNow);
    } catch (err) {
        logger.error(err);
    }
};

/**
 * The proof dialog (INST-PLATES D6, the CAF approval modal's pattern): the plate as it will be
 * printed, at the module preview's scale so its size reads against the other sizes, with its
 * size and finish named under it, "Edit Personalization" (back to the module, box unticked) and
 * "Confirm & Add to Cart". InstPlateConfirm portals it onto the page while it is open.
 */
export const InstPlateConfirmDialog = observer(() => {
    const dialog = useRef<HTMLDivElement>(null);
    const size = instPlateStore.size;

    // Focus the dialog so Escape and the tab order start inside it.
    useEffect(() => {
        dialog.current?.focus();
    }, []);

    return (
        <>
            <div className="modal-backdrop show" />
            <div
                ref={dialog}
                className="modal d-block inst-plate-confirm"
                tabIndex={-1}
                role="dialog"
                aria-modal="true"
                aria-labelledby="inst-plate-confirm-title"
                onClick={e => {
                    if ((e.target as HTMLElement).classList.contains('modal')) {
                        instPlateStore.closeConfirm();
                    }
                }}
                onKeyDown={e => {
                    if (e.key === 'Escape') {
                        instPlateStore.closeConfirm();
                    }
                }}
            >
                <div
                    className="modal-dialog modal-dialog-centered"
                    style={{maxWidth: 700}}
                >
                    <div className="modal-content">
                        <div
                            className="modal-header"
                            role="heading"
                            aria-level={1}
                        >
                            <span id="inst-plate-confirm-title">{INST_PLATE_CONFIRM_TITLE}</span>
                            <button
                                type="button"
                                className="btn-close"
                                aria-label="Close"
                                onClick={instPlateStore.closeConfirm}
                            />
                        </div>
                        <div className="modal-body text-center">
                            <p style={{marginBottom: 16}}>{INST_PLATE_CONFIRM_TEXT}</p>
                            <PlatePreview
                                className="inst-plate-preview inst-plate-confirm-preview"
                                content={s.ps.EPContent || ''}
                                width={size.width}
                                height={size.height}
                                heightPt={s.ps.css?.ep_plateHeightPt}
                                maxWidthPx={platePreviewMaxWidthPx(size.width)}
                            />
                            <p className="inst-plate-confirm-size">
                                {INST_PLATE_CONFIRM_SIZE_LABEL} {plateSummary(size, s.ps.ep_id)}
                            </p>
                        </div>
                        <div
                            className="modal-footer"
                            style={{justifyContent: 'center', gap: 8}}
                        >
                            <button
                                type="button"
                                className="btn btn-outline-dark inst-plate-confirm-edit"
                                onClick={instPlateStore.editPersonalization}
                            >
                                Edit Personalization
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary inst-plate-confirm-atc"
                                onClick={confirmAndAddToCart}
                                disabled={s.ui.atc_busy || s.ui.ep_overflowing}
                                aria-busy={s.ui.atc_busy}
                            >
                                Confirm &amp; Add to Cart
                                {s.ui.atc_busy && (
                                    <span
                                        className="pad-left"
                                        aria-hidden="true"
                                    >
                                        <i className="fas fa-circle-notch fa-spin fast-spin fa-checkout-button" />
                                    </span>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
});

const InstPlateConfirm = observer(() => {
    if (!instPlateStore.confirmOpen || typeof document === 'undefined') {
        return null;
    }

    return createPortal(<InstPlateConfirmDialog />, document.body);
});

export default InstPlateConfirm;
